const MAX_BYTES = 300 * 1024;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", quality));
}

/**
 * Accepts PNG/JPG only and returns a file guaranteed to be under 300 KB,
 * re-encoding and downscaling as needed.
 */
export async function compressChatImage(file: File): Promise<File> {
  const type = file.type.toLowerCase();
  if (type !== "image/png" && type !== "image/jpeg" && type !== "image/jpg") {
    throw new Error("Only PNG or JPG images are allowed.");
  }
  if (file.size <= MAX_BYTES) return file;

  const img = await loadImage(file);
  let width = img.naturalWidth;
  let height = img.naturalHeight;
  const maxSide = 1600;
  if (Math.max(width, height) > maxSide) {
    const ratio = maxSide / Math.max(width, height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not process that image.");
    ctx.drawImage(img, 0, 0, width, height);

    const quality = Math.max(0.4, 0.85 - attempt * 0.08);
    const blob = await toBlob(canvas, quality);
    if (blob && blob.size <= MAX_BYTES) {
      return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
    }
    width = Math.round(width * 0.8);
    height = Math.round(height * 0.8);
  }

  throw new Error("That image is too large to compress. Please pick a smaller one.");
}
